import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChefHat, Package, Calendar, Building2, ShieldCheck, Clock } from 'lucide-react'
import { getPublicTraceability } from '../api/client'
import { DARK, TEAL, BG } from '../components/MarketingLayout'

export default function TraceabilityPublicPage() {
  const { token } = useParams<{ token: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-traceability', token],
    queryFn: () => getPublicTraceability(token!).then(r => r.data),
    enabled: !!token,
    retry: false,
  })

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', minHeight: '100vh', background: BG }}>
      <div style={{ background: DARK }} className="py-5">
        <Link to="/" className="flex items-center justify-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: TEAL }}>
            <ChefHat className="w-4.5 h-4.5 text-white" size={18} />
          </div>
          <span className="font-bold text-white text-lg">CukrászDoc</span>
        </Link>
      </div>

      <div className="max-w-lg mx-auto px-6 py-14">
        {isLoading && <p className="text-center text-sm text-gray-500">Betöltés...</p>}

        {isError && (
          <div className="rounded-2xl p-8 text-center bg-white" style={{ border: '1px solid rgba(91,184,160,0.2)' }}>
            <p className="font-semibold" style={{ color: DARK }}>Ez a nyomonkövetési kód nem található.</p>
            <p className="text-sm text-gray-500 mt-2">Lehet, hogy hibás a link, vagy a rekordot időközben törölték.</p>
          </div>
        )}

        {data && (
          <div className="rounded-2xl overflow-hidden bg-white" style={{ border: '1px solid rgba(91,184,160,0.2)' }}>
            <div className="p-6 sm:p-8" style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)` }}>
              <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
                <ShieldCheck size={12} />
                Nyomonkövetési igazolás
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">{data.recipe_name}</h1>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
                <Building2 size={13} className="inline mr-1.5 -mt-0.5" />{data.company_name}
              </p>
              <div className="flex flex-wrap gap-4 mt-3">
                <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    <Calendar size={11} className="inline mr-1 -mt-0.5" />Gyártva
                  </p>
                  <p className="text-sm font-semibold text-white">{data.production_date}</p>
                </div>
                {data.best_before_date && (
                  <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(91,184,160,0.18)' }}>
                    <p className="text-[10px] uppercase tracking-wide" style={{ color: TEAL }}>
                      <Clock size={11} className="inline mr-1 -mt-0.5" />Fogyasztható
                    </p>
                    <p className="text-sm font-semibold text-white">{data.best_before_date}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-wide mb-4" style={{ color: DARK, letterSpacing: '0.05em' }}>
                Felhasznált alapanyag-tételek
              </p>
              {data.items.length === 0 ? (
                <p className="text-sm text-gray-400">Ehhez a gyártáshoz nincs rögzített alapanyag-tétel adat.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {data.items.map((item: any, i: number) => (
                    <div key={i} className="rounded-xl p-4" style={{ background: BG }}>
                      <div className="flex items-center gap-2 mb-1">
                        <Package size={14} style={{ color: TEAL }} />
                        <span className="font-semibold text-sm" style={{ color: DARK }}>{item.ingredient_name}</span>
                      </div>
                      {item.supplier_name ? (
                        <p className="text-xs text-gray-500 pl-5">
                          {item.supplier_name}
                          {item.batch_number && <> · LOT: {item.batch_number}</>}
                          {item.invoice_number && <> · számla: {item.invoice_number}</>}
                          {item.received_at && <> · érkezett: {item.received_at}</>}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400 pl-5">Nincs rögzített beszállítói tétel</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-8">
          Ezt az oldalt a <Link to="/" style={{ color: TEAL }} className="hover:underline">CukrászDoc</Link> generálta automatikusan.
        </p>
      </div>
    </div>
  )
}
